import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from "rxjs";
import { Router } from "@angular/router";
import { FormGroup } from "@angular/forms";
import { appendHttps, isFormSubmitted } from "../../common/utilities/FormsHelper";
import { Field } from "../../common/enums/Field";
import { ValidationErrorType } from "../../common/enums/ValidationErrorType";
import { SocialMedia } from "../../common/interfaces/SocialMedia";
import { SocialMediaPlatform } from "../../common/enums/SocialMediaPlatform";
import { MerchantApi } from "../../common/apis/merchant-api";
import { EmailApi } from "../../common/apis/email-api";
import { MerchantLoginService } from "../../common/services/merchant-login.service";
import { BaseApiService } from "../../common/services/base-api-service";
import { MerchantPhone } from "../../common/interfaces/MerchantPhone";

@Injectable({
  providedIn: 'root'
})
export class SignupService {
  public currentStepBs: BehaviorSubject<Step> = new BehaviorSubject<Step>(Step.BASIC_INFO)
  public currentStep$: Observable<Step> = this.currentStepBs.asObservable()

  public disableBackButton = false
  public disableNextButton = false
  public loadingNextButton = false

  public signUpForm: FormGroup
  public nameErrorMessage: string | null = null
  public businessNameErrorMessage: string | null = null
  public emailErrorMessage: string | null = null
  public passwordErrorMessage: string | null = null
  public signUpFormErrorMessage: string | null = null
  public enableSignUpFormFields: boolean = true

  // Verify Email
  public emailVerificationErrorMessage: string | null = null
  public emailVerificationSuccessMessage: string | null = null

  // Login Form
  public loginForm: FormGroup
  public loginEmailErrorMessage: string | null = null
  public loginPasswordErrorMessage: string | null = null
  public loginFormErrorMessage: string | null = null
  public enableLoginFormFields: boolean = true


  // More Info
  public moreInfoForm: FormGroup
  public briefNumberErrorMessage: string | null = null
  public taxIdentificationNumberErrorMessage: string | null = null
  public moreInfoFormErrorMessage: string | null = null
  public enableMoreInfoFormFields: boolean = true

  // Contact Form
  public mobileNumberForm: FormGroup
  public socialMediaLinksForm: FormGroup

  public mobileNumberErrorMessage: string | null = null
  public websiteErrorMessage: string | null = null
  public tiktokErrorMessage: string | null = null
  public facebookErrorMessage: string | null = null
  public instagramErrorMessage: string | null = null
  public youtubeErrorMessage: string | null = null
  public xErrorMessage: string | null = null
  public contactFormErrorMessage: string | null = null
  public enableContactFormFields: boolean = true

  public mobileNumberVerified: boolean = false

  constructor(
    private router: Router,
    private apiClient: BaseApiService,
    private merchantApi: MerchantApi,
    private emailApi: EmailApi,
    private merchantLoginService: MerchantLoginService
  ) {
  }

  nextButtonClicked() {
    switch (this.currentStepBs.getValue()) {
      case this.Step.BASIC_INFO:
        // this.signUpFormSubmitted();
        if (isFormSubmitted(this.signUpForm)) {
          this.loadingNextButton = true
          this.enableSignUpFormFields = false

          let name = this.signUpForm.get('name')?.value
          let businessName = this.signUpForm.get('businessName')?.value
          let email = this.signUpForm.get('email')?.value
          let password = this.signUpForm.get('password')?.value

          let registerMerchantRequest = {
            name: name,
            email: email,
            password: password,
            merchantName: businessName
          }

          this.merchantApi.registerMerchant(registerMerchantRequest).subscribe({
            next: () => {
              this.loadingNextButton = false
              this.enableSignUpFormFields = true
              this.resetSignUpFormErrorMessages()

              //Store form information in local storage & read it from confirmation page when initialized with variable.
              localStorage.setItem("name", name)
              localStorage.setItem("businessName", businessName)
              localStorage.setItem("email", email)
              localStorage.setItem("password", password)

              this.currentStepBs.next(this.Step.EMAIL_CONFIRMATION);
              this.router.navigate(["/merchants/signup/email-confirmation"])
            },
            error: error => {
              this.loadingNextButton = false
              this.enableSignUpFormFields = true
              this.resetSignUpFormErrorMessages()

              if (Array.isArray(error)) {
                // Handle each error separately
                console.log("error is an array")
                error.map(singleError => {
                  console.log(singleError.errorType)
                  console.log(singleError.field)
                  if (singleError.field == Field.NAME) {
                    if (singleError.errorType == ValidationErrorType.INVALID_FORMAT) {
                      this.nameErrorMessage = $localize`Invalid name format.`
                    } else if (singleError.errorType == ValidationErrorType.REQUIRED) {
                      this.nameErrorMessage = $localize`Name is required.`
                    }
                  }
                  if (singleError.field == Field.MERCHANT_NAME) {
                    if (singleError.errorType == ValidationErrorType.INVALID_FORMAT) {
                      this.businessNameErrorMessage = $localize`Invalid business name format.`
                    } else if (singleError.errorType == ValidationErrorType.REQUIRED) {
                      this.businessNameErrorMessage = $localize`Business name is required.`
                    }
                  }
                  if (singleError.field == Field.EMAIL) {
                    if (singleError.errorType == ValidationErrorType.DUPLICATE) {
                      this.emailErrorMessage = $localize`This email is already registered.`
                    } else if (singleError.errorType == ValidationErrorType.INVALID_FORMAT) {
                      this.emailErrorMessage = $localize`Invalid email format.`
                    } else if (singleError.errorType == ValidationErrorType.REQUIRED) {
                      this.emailErrorMessage = $localize`Email is required.`
                    }
                  }
                  if (singleError.field == Field.PASSWORD) {
                    if (singleError.errorType == ValidationErrorType.INVALID_FORMAT) {
                      this.passwordErrorMessage = $localize`Invalid password format.`
                    } else if (singleError.errorType == ValidationErrorType.REQUIRED) {
                      this.passwordErrorMessage = $localize`Password is required.`
                    }
                  }
                });
              } else {
                console.log("error is NOT an array")
                console.log(error)
                //TODO: Check if type of error is string before printing
                this.signUpFormErrorMessage = $localize`Something went wrong. Please try again later or contact support.`
              }
            },
            complete: () => {
              // TODO: Remove if not needed.
            }
          })
        }
        break;
      case this.Step.EMAIL_CONFIRMATION:
        // TODO: Confirm Email with server.
        // This step will never happen as the next button should be disabled and the website is waiting for the customer
        // to click on the link from the email.
        // this.currentStepBs.next(this.Step.MERCHANT_MORE_INFO);
        // this.router.navigate(["/merchants/signup/more-info"])
        break;
      case this.Step.MERCHANT_LOGIN:
        // TODO: Login and on success, go to next.
        if (isFormSubmitted(this.loginForm)) {
          this.loadingNextButton = true
          this.enableLoginFormFields = false

          let email = this.loginForm.get('email')?.value
          let password = this.loginForm.get('password')?.value


          this.merchantApi.merchantLogin(email, password).subscribe({
            next: () => {
              this.loadingNextButton = false
              this.enableLoginFormFields = true
              this.resetLoginFormErrorMessages()

              this.currentStepBs.next(this.Step.MERCHANT_MORE_INFO);
              this.router.navigate(["/merchants/signup/more-info"])
            },
            error: error => {
              this.loadingNextButton = false
              this.enableLoginFormFields = true
              this.resetSignUpFormErrorMessages()

              if (Array.isArray(error)) {
                // Handle each error separately
                console.log("error is an array")
                error.map(singleError => {
                  console.log(singleError.errorType)
                  console.log(singleError.field)

                  if (singleError.field == Field.EMAIL) {
                    if (singleError.errorType == ValidationErrorType.DUPLICATE) {
                      this.loginEmailErrorMessage = $localize`This email is already registered.`
                    } else if (singleError.errorType == ValidationErrorType.INVALID_FORMAT) {
                      this.loginEmailErrorMessage = $localize`Invalid email format.`
                    } else if (singleError.errorType == ValidationErrorType.REQUIRED) {
                      this.loginEmailErrorMessage = $localize`Email is required.`
                    }
                  }
                  if (singleError.field == Field.PASSWORD) {
                    if (singleError.errorType == ValidationErrorType.INVALID_FORMAT) {
                      this.loginPasswordErrorMessage = $localize`Invalid password format.`
                    } else if (singleError.errorType == ValidationErrorType.REQUIRED) {
                      this.loginPasswordErrorMessage = $localize`Password is required.`
                    }
                  }
                });
              } else {
                console.log("error is NOT an array")
                console.log(error)

                // TODO: Check if type of error is string before printing
                if (error === ValidationErrorType.UNAUTHORIZED)
                  this.loginFormErrorMessage = $localize`Incorrect username and/or password.`
                else
                  this.loginFormErrorMessage = $localize`Something went wrong. Please try again later or contact support.`
              }
            },
            complete: () => {
              // TODO: Remove if not needed.
            }
          });
        }
        break
      case this.Step.MERCHANT_MORE_INFO:
        if (isFormSubmitted(this.moreInfoForm)) {
          let profilePictureUrl = this.moreInfoForm.get('logoImageURL')?.value
          let coverPhotoUrl = this.moreInfoForm.get('coverPhotoURL')?.value
          let brief = this.moreInfoForm.get('brief')?.value
          let taxIdentificationNumber = this.moreInfoForm.get('taxIdentificationNumber')?.value
          let images = this.moreInfoForm.get('imageURLs')?.value

          this.loadingNextButton = true
          this.merchantApi.updateMerchantInfo(
            profilePictureUrl,
            coverPhotoUrl,
            brief,
            taxIdentificationNumber,
            images
          ).subscribe({
            next: () => {
              this.loadingNextButton = false
              this.enableMoreInfoFormFields = true
              this.resetMoreInfoFormErrorMessages()

              this.currentStepBs.next(this.Step.MERCHANT_CONTACT_INFO);
              this.router.navigate(["/merchants/signup/contact-info"])
            },
            error: error => {
              this.loadingNextButton = false
              this.enableMoreInfoFormFields = true
              this.resetMoreInfoFormErrorMessages()

              if (Array.isArray(error)) {
                // Handle each error separately
                error.map(singleError => {
                  console.log(singleError.errorType)
                  console.log(singleError.field)

                  if (singleError.field == Field.BRIEF) {
                    if (singleError.errorType == ValidationErrorType.INVALID_FORMAT) {
                      this.briefNumberErrorMessage = $localize`Invalid brief format.`
                    } else if (singleError.errorType == ValidationErrorType.REQUIRED) {
                      this.briefNumberErrorMessage = $localize`Brief is required.`
                    }
                  }
                  if (singleError.field == Field.TAX_IDENTIFICATION_NUMBER) {
                    if (singleError.errorType == ValidationErrorType.INVALID_FORMAT) {
                      this.taxIdentificationNumberErrorMessage = $localize`Invalid tax identification number format.`
                    } else if (singleError.errorType == ValidationErrorType.REQUIRED) {
                      this.taxIdentificationNumberErrorMessage = $localize`Tax identification number is required.`
                    }
                  }
                });
              } else {
                // TODO: Check if type of error is string before printing
                if (error === ValidationErrorType.UNAUTHORIZED) {
                  this.currentStepBs.next(this.Step.MERCHANT_LOGIN);
                  this.router.navigate(["/merchants/signup/login"])
                } else
                  this.moreInfoFormErrorMessage = $localize`Something went wrong. Please try again later or contact support.`
              }
            },
            complete: () => {
              // TODO: Remove if not needed.
            }
          })
        }
        break;
      case this.Step.MERCHANT_CONTACT_INFO:
        // TODO: AAAA Disable checking that the mobile number is verified for now.
        /*if (!this.mobileNumberVerified) {
          this.mobileNumberErrorMessage = $localize`Please verify your mobile number.`

          isFormSubmitted(this.socialMediaLinksForm)
        } else*/ if (isFormSubmitted(this.socialMediaLinksForm) && (/*this.mobileNumberVerified ||*/ isFormSubmitted(this.mobileNumberForm))) {

        // TODO: AAAA Disable checking that the mobile number is verified for now.
        // Temporarily save the mobile number here..
        let countryCode = String(this.mobileNumberForm.get('countryCode')?.value)
        let mobileNumber = String(this.mobileNumberForm.get('mobileNumber')?.value)
        let phones: MerchantPhone[] | undefined = []
        if (countryCode && mobileNumber) {
          console.log("hasWhatsapp == " + this.mobileNumberForm.get('hasWhatsapp')?.value)
          console.log("hasTelegram == " + this.mobileNumberForm.get('hasTelegram')?.value)
          let phone: MerchantPhone = {
            countryCode: countryCode,
            phoneNumber: mobileNumber,
            hasWhatsapp: this.mobileNumberForm.get('hasWhatsapp')?.value,
            hasTelegram: this.mobileNumberForm.get('hasTelegram')?.value
          }
          phones = [phone]
        }

        let websites = undefined
        if (this.socialMediaLinksForm.get('website')?.value) {
          websites = [appendHttps(this.socialMediaLinksForm.get('website')?.value)]
        }

        let socialMediaPlatforms: SocialMedia[] | undefined = []
        if (this.socialMediaLinksForm.get('facebook')?.value) {
          const facebook: SocialMedia = {
            platform: SocialMediaPlatform.FACEBOOK,
            url: appendHttps(this.socialMediaLinksForm.get('facebook')?.value)
          }
          socialMediaPlatforms.push(facebook)
        }
        if (this.socialMediaLinksForm.get('tiktok')?.value) {
          const tiktok: SocialMedia = {
            platform: SocialMediaPlatform.TIKTOK,
            url: appendHttps(this.socialMediaLinksForm.get('tiktok')?.value)
          }
          socialMediaPlatforms.push(tiktok)
        }
        if (this.socialMediaLinksForm.get('youtube')?.value) {
          const youtube: SocialMedia = {
            platform: SocialMediaPlatform.YOUTUBE,
            url: appendHttps(this.socialMediaLinksForm.get('youtube')?.value)
          }
          socialMediaPlatforms.push(youtube)
        }
        if (this.socialMediaLinksForm.get('x')?.value) {
          const x: SocialMedia = {
            platform: SocialMediaPlatform.X,
            url: appendHttps(this.socialMediaLinksForm.get('x')?.value)
          }
          socialMediaPlatforms.push(x)
        }
        if (this.socialMediaLinksForm.get('instagram')?.value) {
          const instagram: SocialMedia = {
            platform: SocialMediaPlatform.INSTAGRAM,
            url: appendHttps(this.socialMediaLinksForm.get('instagram')?.value)
          }
          socialMediaPlatforms.push(instagram)
        }

        if (socialMediaPlatforms.length === 0) {
          socialMediaPlatforms = undefined
        }

        this.merchantApi.updateMerchantInfo(
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            phones,
            websites,
            socialMediaPlatforms
          ).subscribe({
            next: () => {
              localStorage.setItem("countryCode", countryCode)
              localStorage.setItem("mobileNumber", mobileNumber)
              this.loadingNextButton = false
              this.enableContactFormFields = true
              this.resetContactFormErrorMessages()

              // this.currentStepBs.next(this.Step.REVIEWING_ACCOUNT);
              // this.router.navigate(["/merchants/login"])
              this.merchantLoginService.redirectToLogin(this.apiClient.getJwt());
            },
            error: error => {
              this.loadingNextButton = false
              this.enableContactFormFields = true
              this.resetContactFormErrorMessages()

              localStorage.removeItem("countryCode")
              localStorage.removeItem("mobileNumber")

              if (Array.isArray(error)) {
                // Handle each error separately
                console.log("error is an array")
                error.map(singleError => {
                  console.log(singleError.errorType)
                  console.log(singleError.field)

                  this.contactFormErrorMessage = $localize`Something went wrong. Please try again later or contact support.`

                  if (singleError.field == Field.PHONE_NUMBER) {
                    if (singleError.errorType == ValidationErrorType.INVALID_FORMAT) {
                      this.mobileNumberErrorMessage = $localize`Invalid mobile number format.`
                    } else if (singleError.errorType == ValidationErrorType.REQUIRED) {
                      this.mobileNumberErrorMessage = $localize`Mobile number is required.`
                    }
                  }
                });
              } else {
                // TODO: Check if type of error is string before printing
                if (error === ValidationErrorType.UNAUTHORIZED) {
                  this.currentStepBs.next(this.Step.MERCHANT_LOGIN);
                  // this.appNavigator.navigateTo({ target: NavigatorTarget.merchantLogin })
                  this.router.navigate(["/merchants/signup/login"])

                  // this.router.navigate(["/merchants/signup/login"])
                } else {
                  this.contactFormErrorMessage = $localize`Something went wrong. Please try again later or contact support.`
                  this.mobileNumberErrorMessage = $localize`Something went wrong. Please try again later or contact support.`
                }
              }
            },
            complete: () => {
              // TODO: Remove if not needed.
            }
          })
        }


        break;
      case this.Step.REVIEWING_ACCOUNT:
        // Nowhere to go for now. Next button should be hidden.
        break;
    }
  }

  backButtonClicked() {
    switch (this.currentStepBs.getValue()) {
      case this.Step.BASIC_INFO:
        // TODO: Nothing to do. Probably go back to the merchant home page?
        break;
      case this.Step.EMAIL_CONFIRMATION:
        this.currentStepBs.next(this.Step.BASIC_INFO);
        this.router.navigate(["/merchants/signup"])
        break;
      case this.Step.MERCHANT_MORE_INFO:
        // this.currentStepBs.next(Step.BASIC_MERCHANT_INFO);
        // this.router.navigate(["/merchants/signup/email-confirmation"])
        // Can't go back (button should be hidden in this step.)
        break;
      case this.Step.MERCHANT_CONTACT_INFO:
        // TODO: Stop fetching email status.
        this.currentStepBs.next(this.Step.MERCHANT_MORE_INFO);
        this.router.navigate(["/merchants/signup/more-info"])
        break;
      case this.Step.REVIEWING_ACCOUNT:
        this.currentStepBs.next(this.Step.MERCHANT_CONTACT_INFO);
        this.router.navigate(["/merchants/signup/contact-info"])
        break;
    }
  }

  public resendEmailButtonClicked() {
    console.log("resendEmailButtonClicked");
    let email = localStorage.getItem("email")
    if (email) {
      this.emailApi.resendVerificationEmail(email).subscribe({
        next: () => {
          // Email resent successfully
          this.emailVerificationSuccessMessage = $localize`Email resent successfully.`
          this.emailVerificationErrorMessage = null
        },
        error: error => {
          console.log("Error")
          console.log(error)
          if (Array.isArray(error)) {
            // Handle each error separately
            console.log("error is an array")
            error.map(singleError => {
              if(singleError.rawMessage === "/User/Email/Try-Later")
              {
                this.emailVerificationErrorMessage = $localize`A request is already being processed. Please check your email, including the junk folder or try again later 5 minutes later.`
              }
            })
          }
          else {
            if(error.rawMessage === "/User/Email/Try-Later")
              this.emailVerificationErrorMessage = $localize`A request is already being processed. Please check your email, including the junk folder or try again later 5 minutes later.`
            else
              this.emailVerificationErrorMessage = $localize`An unknown error has occurred. Please try again later or contact support.`
          }

        }
      })
    }
  }

  public verifyEmail(token: string) {
    this.emailApi.verifyEmail(token).subscribe({
      next: () => {
        // Proceed to next step

        let email = localStorage.getItem("email")
        let password = localStorage.getItem("password")
        if (email && password) {
          this.merchantApi.merchantLogin(email, password).subscribe({
            next: () => {
              console.log("Logged in successfully")
              this.emailVerificationSuccessMessage = null
              this.currentStepBs.next(this.Step.MERCHANT_MORE_INFO);
              this.router.navigate(["/merchants/signup/more-info"])
            },
            error: error => {
              console.log("Failed to login")
              console.log("Error")
              console.log(error)
              this.currentStepBs.next(this.Step.MERCHANT_LOGIN);
              this.router.navigate(["/merchants/signup/login"])
            }
          })
        } else {
          console.log("Credentials not found.. Go to login")
          this.currentStepBs.next(this.Step.MERCHANT_LOGIN);
          this.router.navigate(["/merchants/signup/login"])
        }
      },
      error: error => {
        console.log("Error")
        console.log(error)
        // TODO Handle this error "/User/Email/Try-Later" here && "/Customer/Email/Try-Later" in the customer registration page.
        this.emailVerificationErrorMessage = $localize`An unknown error has occurred. Please try again later or contact support.`
      }
    })
  }

  public readonly Step = Step;

  private resetSignUpFormErrorMessages() {
    this.nameErrorMessage = null
    this.businessNameErrorMessage = null
    this.emailErrorMessage = null
    this.passwordErrorMessage = null
    this.signUpFormErrorMessage = null
  }

  private resetLoginFormErrorMessages() {
    this.loginEmailErrorMessage = null
    this.loginPasswordErrorMessage = null
    this.loginFormErrorMessage = null
  }

  private resetMoreInfoFormErrorMessages() {
    this.briefNumberErrorMessage = null
    this.taxIdentificationNumberErrorMessage = null
    this.moreInfoFormErrorMessage = null
  }

  private resetContactFormErrorMessages() {
    this.mobileNumberErrorMessage = null
    this.websiteErrorMessage = null
    this.tiktokErrorMessage = null
    this.facebookErrorMessage = null
    this.instagramErrorMessage = null
    this.youtubeErrorMessage = null
    this.xErrorMessage = null
    this.contactFormErrorMessage = null
  }
}

export enum Step {
  BASIC_INFO,
  EMAIL_CONFIRMATION,
  MERCHANT_LOGIN,
  MERCHANT_MORE_INFO,
  MERCHANT_CONTACT_INFO,
  REVIEWING_ACCOUNT
}
